import { observer } from 'mobx-react';
import * as React from 'react';

import { withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { PlotKeyToggleState } from '../../../stores/plot-key-toggle-state';
import { Plot, QuizResultsHelper } from '../../../stores/quiz-results-store';
import { coordinateToTextStyle, plotKeyToLineStyle } from '../competency-radar/competency-radar';
import { SVGBuilder } from '../svg-builder/svg-builder';
import { ResolvedGroup } from '../competency-bars/common';

export interface SkillGroupRadarProps extends WithI18nProps {
  qr: QuizResultsHelper;
  plotKeyToggleState: PlotKeyToggleState;
  selectedGroup?: string;
  gridRadii?: number[];
  onGroupClick(heading: string): void;
}

@observer
class SkillGroupRadar0 extends React.Component<SkillGroupRadarProps> {
  private resolveGroups(): ResolvedGroup[] | undefined {
    const { qr } = this.props;
    const skillGroups = qr.getSkillGroups();
    return (skillGroups !== undefined) ?
      Object.keys(skillGroups).map(key => ({
        heading: key,
        skillIds: skillGroups[key]
      })
      ) : undefined;
  }

  private computeGroupScore(plot: keyof Plot, skillIds: string[]): number {
    if (skillIds.length === 0) { return 0; }
    const { qr } = this.props;
    const scores = skillIds.map(id => qr.score(plot, { kind: 'skill', skillId: id }));
    return scores.reduce((sum, s) => sum + s, 0) / scores.length;
  }

  private groupScoreText(skillIds: string[]): string {
    const actual = this.computeGroupScore('actual', skillIds);
    const expected = this.computeGroupScore('expected', skillIds);
    if (expected === 0) { return 'N/A'; }
    return `${Math.round((actual / expected) * 100)} %`;
  }

  render() {
    const { qr, plotKeyToggleState, selectedGroup, gridRadii: gridRadiiProp } = this.props;

    const resolvedGroups = this.resolveGroups();
    if (resolvedGroups === undefined) return null;
    const n = resolvedGroups.length;
    if (n === 0) { return null; }

    const gridRadii = gridRadiiProp ? gridRadiiProp.slice() : [20, 40, 60, 80, 100];

    const viewBox = { width: 400, height: 300 };
    const labelsPlacement = {
      actualScoresRadius: 120,
      competencyLabels: { ellipseSemiAxisX: 130, ellipseSemiAxisY: 130 }
    };

    const groupAngles = resolvedGroups.map((_, i) => (i * 2.0 * Math.PI) / n);

    const atRadiusAngle = (value: number, i: number) => ({
      x: value * Math.sin(groupAngles[i]),
      y: -value * Math.cos(groupAngles[i])
    });

    const coordsLabel = groupAngles.map((_, i) => atRadiusAngle(labelsPlacement.actualScoresRadius, i));

    const builder = new SVGBuilder(1, {
      posX: (-1 * viewBox.width) / 2,
      posY: (-1 * viewBox.height) / 2,
      dimX: viewBox.width,
      dimY: viewBox.height
    });

    for (const r of gridRadii.reverse()) {
      builder.addCircle({ x: 0, y: 0, r, style: 'background' });
    }

    for (let i = 0; i < n; i++) {
      const pt = atRadiusAngle(100, i);
      builder.addLine({ x1: 0, y1: 0, x2: pt.x, y2: pt.y, style: 'solid', stroke: 'light' });
    }

    for (let i = 0; i < n; i++) {
      const group = resolvedGroups[i];
      const x = atRadiusAngle(100, i).x;
      const y = atRadiusAngle(100, i).y;
      const anchor = coordinateToTextStyle(x, y);
      const coord = coordsLabel[i];
      const percentage = this.groupScoreText(group.skillIds);

      builder.addLabel({
        x: coord.x,
        y: coord.y,
        anchor,
        text: percentage,
        subtext: group.heading.toUpperCase(),
        style: selectedGroup === group.heading ? 'highlighted' : 'regular',
        onClick: () => this.props.onGroupClick(group.heading)
      });
    }

    for (const plotkey of qr.getPlotKeys().filter(p => plotKeyToggleState.isShown(p))) {
      if (!qr.plotExists(plotkey)) { continue; }

      const scores = resolvedGroups.map((g, i) =>
        atRadiusAngle(this.computeGroupScore(plotkey, g.skillIds), i)
      );

      for (let i = 0; i < scores.length - 1; i++) {
        builder.addLine({
          x1: scores[i].x,
          y1: scores[i].y,
          x2: scores[i + 1].x,
          y2: scores[i + 1].y,
          style: plotKeyToLineStyle(plotkey),
          stroke: plotkey === 'previous' ? 'dark' : 'medium'
        });
      }
      builder.addLine({
        x1: scores[scores.length - 1].x,
        y1: scores[scores.length - 1].y,
        x2: scores[0].x,
        y2: scores[0].y,
        style: plotKeyToLineStyle(plotkey),
        stroke: plotkey === 'previous' ? 'dark' : 'medium'
      });

      for (const score of scores) {
        if (plotkey === 'expected') {
          builder.addCircle({ x: score.x, y: score.y, r: 2, style: 'filled' });
        } else if (plotkey === 'group') {
          builder.addCircle({ x: score.x, y: score.y, r: 2, style: 'shaded' });
        } else if (plotkey === 'industry') {
          builder.addTriangle({ x: score.x, y: score.y, size: 2 });
        }
      }
    }

    return builder.build();
  }
}

export const SkillGroupRadar = withI18n(SkillGroupRadar0);



// WEBPACK FOOTER //
// ./src/ui/components/skill-group-radar/skill-group-radar.tsx